import {List, Map} from 'immutable'
import {useMemo, useState} from 'react'
import {useMutation, useQuery} from '@apollo/client'

import {DISMISS_SUGGESTED_DEPUTY} from '../../../../graphql/mutations/deputy'
import {SUGGESTED_DEPUTIES} from '../../../../graphql/queries/deputy'
import {cleanGraphQLResponse} from '../../../../lib/graphQLTools'
import MixpanelLogger from '../../../../lib/MixpanelLogger'
import routerUtils from '../../../../lib/routerUtils'
import {findContactDeputy, showExistingDeputy} from '../../../../lib/deputyPermissionTools'

const useSuggestedDeputies = ({
  context,
  deputies = List(),
  everplanId,
  firstGroupId,
  itemResponses = List(),
  numberToDisplay = 5,
  onAddDeputy,
  onShareWithDeputy,
  vaultExtension = Map()
}) => {
  const [dismissedContactIds, setDismissedContactIds] = useState(List())

  const everplanIdInt = Number.parseInt(everplanId, 10)
  const shouldSkip = !Number.isFinite(everplanIdInt) || numberToDisplay <= 0

  const {data} = useQuery(SUGGESTED_DEPUTIES, {
    skip: shouldSkip,
    variables: {everplanId: everplanIdInt}
  })

  const [dismissSuggestedDeputy] = useMutation(DISMISS_SUGGESTED_DEPUTY, {
    update: (cache, {data: mutationData}) => {
      cache.writeQuery({
        query: SUGGESTED_DEPUTIES,
        data: {suggestedDeputies: mutationData.dismissSuggestedDeputy},
        variables: {everplanId: everplanIdInt}
      })
    }
  })

  const suggestedDeputies = useMemo(
    () => cleanGraphQLResponse(data, 'suggestedDeputies'),
    [data]
  )

  // Contacts who are not deputies yet are suggested so they can be invited; contacts who
  // already are deputies are only suggested when they cannot see the arrangements yet.
  const visibleSuggestedDeputies = suggestedDeputies.filter(contact => (
    !dismissedContactIds.includes(contact.get('id')) && (
      findContactDeputy({contact, deputies}).isEmpty() ||
        showExistingDeputy({contact, firstGroupId, itemResponses, vaultExtension, deputies})
    )
  )).take(numberToDisplay)

  const onInvite = contact => {
    const contactDeputy = findContactDeputy({contact, deputies})

    if (contactDeputy.isEmpty()) {
      MixpanelLogger.track(
        'click_add_deputy',
        {context, deputy_count: deputies.size, source: 'suggested'}
      )

      if (onAddDeputy)
        return onAddDeputy(contact)

      return routerUtils.push(`cm/deputies/new/${contact.get('id')}?source=suggested`)
    }

    if (onShareWithDeputy)
      return onShareWithDeputy(contactDeputy)

    return routerUtils.push(`cm/deputies/${contactDeputy.get('id')}/manage-permissions?context=${context}`)
  }

  const onDismiss = contact => {
    setDismissedContactIds(prev => prev.push(contact.get('id')))

    dismissSuggestedDeputy({variables: {id: contact.get('id')}}).catch(() => {})
  }

  return {
    onDismiss,
    onInvite,
    suggestedDeputies: visibleSuggestedDeputies
  }
}

export default useSuggestedDeputies
